<template>
  <div class="visitas-page">
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="visitas-shell">
      <!-- Header -->
      <header class="page-header fade-in">
        <div class="page-header-left">
          <div class="page-icon-box">
            <v-icon color="#F2F2ED" size="22">mdi-dumbbell</v-icon>
          </div>
          <div>
            <h1 class="page-title">Control de Acceso</h1>
            <p class="page-sub">Registro de visitas del gimnasio</p>
          </div>
        </div>

        <div class="page-header-right">
          <div class="search-wrapper">
            <busqueda-miembro @seleccionado="onMiembroSeleccionado" />
          </div>
          <button v-if="miembro" class="icon-btn" @click="limpiarSeleccion">
            <v-icon size="18" color="#8C8C93">mdi-refresh</v-icon>
          </button>
        </div>
      </header>

      <!-- Contenido -->
      <div class="visitas-grid">
        <!-- Panel principal -->
        <div class="main-panel card-flat fade-in delay-100">
          <!-- Sin selección -->
          <div v-if="!miembro" class="empty-state">
            <div class="empty-icon">
              <v-icon size="36" color="#5C5C62">mdi-account-search</v-icon>
            </div>
            <h2 class="empty-title">Busca un miembro</h2>
            <p class="empty-desc">Usa el buscador para encontrar al miembro y registrar su visita</p>
            <button class="btn-primary mt-lg" @click="mostrarDialogoRegular = true">
              <v-icon size="16" class="mr-2">mdi-account-cash</v-icon>
              Visita sin membresía
            </button>
          </div>

          <!-- Con miembro seleccionado -->
          <div v-else class="member-selected">
            <div class="member-head">
              <div class="member-avatar-wrapper">
                <img :src="urlImagen(miembro.imagen)" alt="Avatar" class="member-avatar-img"
                  @error="$event.target.src = 'https://via.placeholder.com/140'" />
                <span class="member-status-dot" :class="estadoClass(miembro.estado)"></span>
              </div>

              <div class="member-info">
                <div class="member-name-row">
                  <h2 class="member-name">{{ miembro.nombre }}</h2>
                  <span class="status-tag" :class="estadoBadgeClass(miembro.estado)">
                    <v-icon size="12" class="mr-1">{{ estadoIcon(miembro.estado) }}</v-icon>
                    {{ miembro.estado || 'SIN MEMBRESÍA' }}
                  </span>
                </div>

                <div class="member-meta">
                  <span class="meta-item">
                    <v-icon size="14" class="mr-1">mdi-identifier</v-icon>
                    Matrícula <strong>{{ miembro.matricula }}</strong>
                  </span>
                  <span v-if="miembro.telefono" class="meta-item">
                    <v-icon size="14" class="mr-1">mdi-phone</v-icon>
                    {{ miembro.telefono }}
                  </span>
                </div>
              </div>
            </div>

            <div class="section-divider"></div>

            <!-- Membresía -->
            <div v-if="miembro.membresia" class="membership-card">
              <div class="membership-head">
                <div class="membership-icon">
                  <v-icon size="18" color="#C8FF4D">mdi-certificate</v-icon>
                </div>
                <div>
                  <h3 class="membership-name">{{ miembro.membresia }}</h3>
                  <p class="membership-plan">Plan activo</p>
                </div>
              </div>

              <div class="membership-dates">
                <div class="date-row">
                  <span class="date-label"><v-icon size="14" class="mr-1">mdi-calendar-start</v-icon>Inicio</span>
                  <span class="date-value">{{ fechaFormateada(miembro.fechaInicio) }}</span>
                </div>
                <div class="date-row">
                  <span class="date-label"><v-icon size="14" class="mr-1">mdi-calendar-end</v-icon>Vence</span>
                  <span class="date-value">{{ fechaFormateada(miembro.fechaFin) }}</span>
                </div>
              </div>

              <div v-if="miembro.fechaFin" class="progress-block">
                <div class="progress-labels">
                  <span class="progress-label">{{ diasRestantes(miembro.fechaFin) }} días restantes</span>
                  <span class="progress-pct">{{ diasProgreso(miembro.fechaFin) }}%</span>
                </div>
                <div class="progress-track">
                  <div class="progress-fill" :class="diasProgressClass(miembro.fechaFin)"
                    :style="{ width: diasProgreso(miembro.fechaFin) + '%' }"></div>
                </div>
              </div>
            </div>

            <!-- Sin membresía -->
            <div v-if="!miembro.membresia || miembro.estado === 'VENCIDO'" class="alert-box">
              <v-icon size="18" color="#FFB84D">mdi-alert-circle</v-icon>
              <div>
                <div class="alert-title">Membresía requerida</div>
                <div class="alert-text">Este miembro no tiene una membresía activa. Es necesario renovar para registrar visitas.</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Panel de acciones -->
        <div class="actions-panel card-flat fade-in delay-200">
          <div v-if="!miembro" class="empty-state empty-state-compact">
            <div class="empty-icon">
              <v-icon size="28" color="#5C5C62">mdi-hand-pointing-right</v-icon>
            </div>
            <h3 class="empty-title-sm">Acciones rápidas</h3>
            <p class="empty-desc-sm">Selecciona un miembro para ver las opciones disponibles</p>
          </div>

          <div v-else>
            <div class="section-eyebrow">
              <span class="tick-mark"></span>
              Acciones disponibles
            </div>

            <div class="actions-list">
              <button v-if="miembro.estado === 'ACTIVO'" class="action-btn action-btn-accent"
                @click="abrirRegistrarVisita">
                <div class="action-icon">
                  <v-icon size="18" color="#0E0E10">mdi-check-circle</v-icon>
                </div>
                <div class="action-text">
                  <div class="action-title">Registrar visita</div>
                  <div class="action-subtitle">Confirmar acceso</div>
                </div>
              </button>

              <button v-else-if="!miembro.membresia || miembro.estado === 'VENCIDO'"
                class="action-btn action-btn-warning" @click="abrirRealizarPago">
                <div class="action-icon action-icon-warning">
                  <v-icon size="18" color="#0E0E10">mdi-credit-card-refresh</v-icon>
                </div>
                <div class="action-text">
                  <div class="action-title">Renovar membresía</div>
                  <div class="action-subtitle">Activar plan</div>
                </div>
              </button>
            </div>

            <div class="section-divider"></div>

            <button class="btn-ghost btn-block" @click="limpiarSeleccion">
              <v-icon size="15" class="mr-2">mdi-close-circle</v-icon>
              Limpiar selección
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Diálogo: Registrar Visita -->
    <v-dialog v-model="mostrarRegistrarVisita" max-width="440" v-if="miembro">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Confirmar visita</h3>
          <p class="modal-sub">Verificación de acceso al gimnasio</p>
        </div>

        <div class="modal-body">
          <div class="confirm-content">
            <img :src="urlImagen(miembro.imagen)" alt="Avatar" class="confirm-avatar"
              @error="$event.target.src = 'https://via.placeholder.com/100'" />
            <h3 class="confirm-name">{{ miembro.nombre }}</h3>

            <div class="confirm-details">
              <div class="confirm-row">
                <v-icon size="14" class="mr-2" color="#8C8C93">mdi-identifier</v-icon>
                <span>Matrícula <strong>{{ miembro.matricula }}</strong></span>
              </div>
              <div class="confirm-row">
                <v-icon size="14" class="mr-2" color="#8C8C93">mdi-certificate</v-icon>
                <span>Plan <strong>{{ miembro.membresia }}</strong></span>
              </div>
              <div class="confirm-row">
                <v-icon size="14" class="mr-2" color="#8C8C93">mdi-clock-outline</v-icon>
                <span>Hora <strong>{{ horaActual }}</strong></span>
              </div>
            </div>

            <div class="info-box">
              La visita se registrará automáticamente
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="cerrarDialogo">Cancelar</button>
          <button class="btn-primary" @click="registrar" :disabled="cargando">
            <span v-if="!cargando">Confirmar visita</span>
            <span v-else class="loading-inline"><span class="spinner-sm"></span>Procesando</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Diálogo: Realizar Pago -->
    <v-dialog v-model="mostrarRealizarPago" persistent max-width="700">
      <realizar-pago :matricula="matricula" :miembro="miembro" @cerrar="cerrarDialogoPago" @pagado="onPagado" />
    </v-dialog>

    <!-- Diálogo: Visita Regular -->
    <v-dialog v-model="mostrarDialogoRegular" max-width="440">
      <div class="modal-panel">
        <div class="modal-head">
          <h3 class="modal-title">Visita sin membresía</h3>
          <p class="modal-sub">Registro de visita ocasional</p>
        </div>

        <div class="modal-body">
          <p class="modal-text">Registra el pago de la visita regular (sin membresía activa)</p>

          <div class="field">
            <label class="field-label">Monto del pago</label>
            <div class="field-input-wrapper">
              <span class="field-prefix">$</span>
              <input v-model="pagoVisita" type="number" class="field-input" placeholder="0.00" />
            </div>
          </div>

          <div class="alert-box mt-lg">
            <v-icon size="18" color="#FFB84D">mdi-alert</v-icon>
            <div class="alert-text">Esta visita no consume días de membresía</div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-ghost" @click="mostrarDialogoRegular = false">Cancelar</button>
          <button class="btn-primary" @click="registrarVisitaRegular" :disabled="cargando || !pagoVisita">
            <span v-if="!cargando">Registrar pago</span>
            <span v-else class="loading-inline"><span class="spinner-sm"></span>Procesando</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="mostrarMensaje" :timeout="4000" :color="mensaje.color" top right>
      <div class="flex items-center gap-md">
        <v-icon color="white">{{ mensajeIcon }}</v-icon>
        <div>
          <div class="font-semibold">{{ mensaje.titulo || 'Notificación' }}</div>
          <div class="text-sm">{{ mensaje.texto }}</div>
        </div>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon small @click="mostrarMensaje = false" v-bind="attrs">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import BusquedaMiembro from "../Miembros/BusquedaMiembro.vue";
import RealizarPago from "../Miembros/RealizarPago.vue";
import Utiles from "../../Servicios/Utiles";
import HttpService from "../../Servicios/HttpService";
import { formatearFechaHora } from '@/utils/fechas';

export default {
  name: "RegistrarVisita",
  components: { BusquedaMiembro, RealizarPago },

  data: () => ({
    miembro: null,
    mostrarRegistrarVisita: false,
    mostrarRealizarPago: false,
    mostrarDialogoRegular: false,
    cargando: false,
    mensaje: {},
    mostrarMensaje: false,
    matricula: "",
    pagoVisita: "",
    stats: {
      activos: 150,
      hoy: 42,
      porVencer: 8
    },
    horaActual: new Date().toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }),

  computed: {
    mensajeIcon() {
      switch (this.mensaje.color) {
        case 'success': return 'mdi-check-circle';
        case 'error': return 'mdi-alert-circle';
        case 'warning': return 'mdi-alert';
        case 'info': return 'mdi-information';
        default: return 'mdi-bell';
      }
    }
  },

  methods: {
    fechaFormateada(fecha) {
      if (!fecha) return 'N/A';
      const date = new Date(fecha);
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    diasRestantes(fechaFin) {
      const fecha = new Date(fechaFin);
      const hoy = new Date();
      const diffTime = fecha - hoy;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    },

    diasProgreso(fechaFin) {
      const dias = this.diasRestantes(fechaFin);
      return Math.max(0, Math.min(100, (dias / 30) * 100));
    },

    diasProgressClass(fechaFin) {
      const dias = this.diasRestantes(fechaFin);
      if (dias > 15) return 'progress-ok';
      if (dias > 7) return 'progress-warn';
      return 'progress-danger';
    },

    estadoIcon(estado) {
      switch (estado) {
        case 'ACTIVO': return 'mdi-check-circle';
        case 'VENCIDO': return 'mdi-alert-circle';
        default: return 'mdi-account-question';
      }
    },

    estadoClass(estado) {
      switch (estado) {
        case 'ACTIVO': return 'status-active';
        case 'VENCIDO': return 'status-expired';
        default: return 'status-inactive';
      }
    },

    estadoBadgeClass(estado) {
      switch (estado) {
        case 'ACTIVO': return 'status-tag-success';
        case 'VENCIDO': return 'status-tag-error';
        default: return 'status-tag-neutral';
      }
    },

    registrarVisitaRegular() {
      if (!this.pagoVisita) return;

      this.cargando = true;
      HttpService.registrar({
        metodo: 'registrar_regular',
        visita: {
          idUsuario: localStorage.getItem('idUsuario'),
          pago: this.pagoVisita
        }
      }, 'visitas.php')
        .then(resultado => {
          if (resultado) {
            this.mostrarDialogoRegular = false;
            this.pagoVisita = "";
            this.mostrarMensajeExito('Visita Regular Registrada', 'El pago ha sido procesado exitosamente');
          }
        })
        .catch(error => {
          this.mostrarMensajeError('Error al Registrar', 'No se pudo procesar la visita regular');
        })
        .finally(() => {
          this.cargando = false;
        });
    },

    abrirRealizarPago() {
      this.matricula = this.miembro.matricula;
      this.mostrarRealizarPago = true;
    },

    onPagado(resultado) {
      if (resultado) {
        this.mostrarRealizarPago = false;
        this.mostrarMensajeExito('Pago Exitoso', 'La membresía ha sido renovada. Ahora puedes registrar visitas.');
        this.miembro = null;
      }
    },

    cerrarDialogoPago() {
      this.mostrarRealizarPago = false;
    },

    cerrarDialogo() {
      this.mostrarRegistrarVisita = false;
    },

    registrar() {
      this.cargando = true;
      let payload = {
        metodo: "registrar",
        visita: {
          idMiembro: this.miembro.id,
          idMembresia: this.miembro.idMembresia,
          idUsuario: localStorage.getItem("idUsuario"),
        },
      };

      HttpService.registrar(payload, "visitas.php")
        .then((resultado) => {
          if (resultado && resultado.success) {
            this.mostrarMensajeExito('Visita Registrada', `${this.miembro.nombre} ha ingresado al gimnasio`);
            this.miembro = null;
            this.mostrarRegistrarVisita = false;
            this.stats.hoy++;
          } else if (resultado && resultado.duplicado) {
            this.mostrarMensajeError('Visita ya registrada', resultado.message);
          } else {
            this.mostrarMensajeError('Error al Registrar', (resultado && resultado.message) || 'No se pudo completar el registro de visita');
          }
        })
        .catch(error => {
          this.mostrarMensajeError('Error al Registrar', 'No se pudo completar el registro de visita');
        })
        .finally(() => {
          this.cargando = false;
        });
    },

    abrirRegistrarVisita() {
      this.horaActual = new Date().toLocaleTimeString('es-MX', {
        hour: '2-digit',
        minute: '2-digit'
      });
      this.mostrarRegistrarVisita = true;
    },

    urlImagen(imagen) {
      return Utiles.generarURL(imagen);
    },

    onMiembroSeleccionado(miembro) {
      this.miembro = miembro;
    },

    limpiarSeleccion() {
      this.miembro = null;
    },

    mostrarMensajeExito(titulo, texto) {
      this.mensaje = {
        titulo: titulo,
        texto: texto,
        color: 'success'
      };
      this.mostrarMensaje = true;
    },

    mostrarMensajeError(titulo, texto) {
      this.mensaje = {
        titulo: titulo,
        texto: texto,
        color: 'error'
      };
      this.mostrarMensaje = true;
    }
  },

  mounted() {
    setInterval(() => {
      this.horaActual = new Date().toLocaleTimeString('es-MX', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }, 60000);
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

/* ===== TOKENS (mismos del sistema) ===== */
.visitas-page {
  --bg: #0E0E10;
  --card: #17171A;
  --card-hover: #1B1B1F;
  --card-border: #26262A;
  --text-primary: #F2F2ED;
  --text-secondary: #8C8C93;
  --text-tertiary: #5C5C62;
  --accent: #C8FF4D;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --warning: #FFB84D;
  --error: #FF6B6B;
  --radius: 4px;

  --font-display: 'Space Grotesk', sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
}

.visitas-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(242, 242, 237, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.visitas-shell {
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  padding: 22px 26px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 32px;
}

.page-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-icon-box {
  width: 42px;
  height: 42px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0 0 2px;
}

.page-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.page-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  max-width: 460px;
  min-width: 240px;
}

.search-wrapper {
  flex: 1;
}

.icon-btn {
  width: 36px;
  height: 36px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease;
  flex-shrink: 0;
}

.icon-btn:hover {
  border-color: var(--text-tertiary);
}

/* ===== GRID ===== */
.visitas-grid {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 16px;
  align-items: start;
}

.card-flat {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 28px;
}

/* ===== EMPTY STATE ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 20px;
}

.empty-state-compact {
  padding: 32px 12px;
}

.empty-icon {
  width: 60px;
  height: 60px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}

.empty-title {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 6px;
}

.empty-title-sm {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 6px;
  color: var(--text-primary);
}

.empty-desc {
  font-size: 13px;
  color: var(--text-secondary);
  max-width: 300px;
  margin: 0;
}

.empty-desc-sm {
  font-size: 12.5px;
  color: var(--text-tertiary);
  margin: 0;
}

/* ===== MEMBER SELECTED ===== */
.member-head {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.member-avatar-wrapper {
  position: relative;
  width: 88px;
  height: 88px;
  flex-shrink: 0;
}

.member-avatar-img {
  width: 100%;
  height: 100%;
  border-radius: var(--radius);
  object-fit: cover;
  border: 1px solid var(--card-border);
}

.member-status-dot {
  position: absolute;
  bottom: -3px;
  right: -3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid var(--card);
}

.member-status-dot.status-active {
  background: var(--accent);
}

.member-status-dot.status-expired {
  background: var(--error);
}

.member-status-dot.status-inactive {
  background: var(--text-tertiary);
}

.member-info {
  flex: 1;
  min-width: 200px;
}

.member-name-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.member-name {
  font-family: var(--font-display);
  font-size: 19px;
  font-weight: 700;
  margin: 0;
}

.member-meta {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  font-size: 12.5px;
  color: var(--text-secondary);
}

.meta-item strong {
  color: var(--text-primary);
  font-weight: 600;
  margin-left: 4px;
}

.section-divider {
  height: 1px;
  background: var(--card-border);
  margin: 22px 0;
}

/* ===== MEMBERSHIP CARD ===== */
.membership-card {
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 20px;
}

.membership-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.membership-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius);
  background: var(--accent-dim);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.membership-name {
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 700;
  margin: 0;
}

.membership-plan {
  font-size: 12px;
  color: var(--text-tertiary);
  margin: 2px 0 0;
}

.membership-dates {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid var(--card-border);
}

.date-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.date-label {
  display: inline-flex;
  align-items: center;
  font-size: 12.5px;
  color: var(--text-secondary);
}

.date-value {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--text-primary);
}

.progress-block {
  margin-top: 18px;
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.progress-label {
  font-size: 11.5px;
  color: var(--text-secondary);
}

.progress-pct {
  font-family: var(--font-mono);
  font-size: 11.5px;
  color: var(--text-primary);
}

.progress-track {
  height: 4px;
  background: var(--card-border);
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 2px;
}

.progress-fill.progress-ok {
  background: var(--accent);
}

.progress-fill.progress-warn {
  background: var(--warning);
}

.progress-fill.progress-danger {
  background: var(--error);
}

/* ===== ALERT ===== */
.alert-box {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(255, 184, 77, 0.08);
  border: 1px solid rgba(255, 184, 77, 0.25);
  border-radius: var(--radius);
  padding: 14px 16px;
  margin-top: 20px;
}

.alert-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.alert-text {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.info-box {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 12px 14px;
  font-size: 12.5px;
  color: var(--text-secondary);
  margin-top: 18px;
  text-align: center;
}

/* ===== ACTIONS ===== */
.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 16px;
}

.tick-mark {
  width: 12px;
  height: 1px;
  background: var(--accent);
}

.actions-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  border: 1px solid var(--card-border);
  background: var(--card);
  border-radius: var(--radius);
  padding: 14px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, transform 0.15s ease;
}

.action-btn:hover {
  transform: translateY(-1px);
}

.action-btn-accent {
  border-color: rgba(200, 255, 77, 0.3);
}

.action-btn-accent:hover {
  border-color: var(--accent);
}

.action-btn-warning {
  border-color: rgba(255, 184, 77, 0.3);
}

.action-btn-warning:hover {
  border-color: var(--warning);
}

.action-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius);
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.action-icon-warning {
  background: var(--warning);
}

.action-title {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-primary);
}

.action-subtitle {
  font-size: 11.5px;
  color: var(--text-tertiary);
  margin-top: 1px;
}

/* ===== STATUS TAGS ===== */
.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: var(--radius);
  font-size: 10.5px;
  font-weight: 600;
  font-family: var(--font-mono);
  letter-spacing: 0.03em;
}

.status-tag-success {
  background: var(--accent-dim);
  color: var(--accent);
}

.status-tag-error {
  background: rgba(255, 107, 107, 0.12);
  color: var(--error);
}

.status-tag-neutral {
  background: rgba(140, 140, 147, 0.14);
  color: var(--text-secondary);
}

/* ===== BUTTONS ===== */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 11px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(1.08);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: default;
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 11px 18px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.btn-block {
  width: 100%;
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.spinner-sm {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(14, 14, 16, 0.3);
  border-top-color: var(--bg);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== MODAL ===== */
.modal-panel {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.modal-head {
  padding: 22px 24px;
  border-bottom: 1px solid var(--card-border);
}

.modal-title {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  margin: 0 0 4px;
}

.modal-sub {
  font-size: 12.5px;
  color: var(--text-secondary);
  margin: 0;
}

.modal-body {
  padding: 24px;
}

.modal-text {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 18px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
}

/* Confirmación de visita */
.confirm-content {
  text-align: center;
}

.confirm-avatar {
  width: 76px;
  height: 76px;
  border-radius: var(--radius);
  object-fit: cover;
  border: 1px solid var(--card-border);
  margin: 0 auto 14px;
}

.confirm-name {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 18px;
}

.confirm-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
}

.confirm-row {
  display: flex;
  align-items: center;
  padding: 9px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  font-size: 12.5px;
  color: var(--text-secondary);
}

.confirm-row strong {
  color: var(--text-primary);
  margin-left: 4px;
}

/* Field (visita regular) */
.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: var(--text-secondary);
  text-transform: uppercase;
}

.field-input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  background: var(--bg);
  padding: 0 12px;
  transition: border-color 0.15s ease;
}

.field-input-wrapper:focus-within {
  border-color: var(--accent);
}

.field-prefix {
  color: var(--text-tertiary);
  font-weight: 600;
  margin-right: 6px;
}

.field-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  padding: 12px 0;
  color: var(--text-primary);
  font-size: 14px;
  font-family: var(--font-body);
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.4s ease both;
}

.delay-100 { animation-delay: 0.05s; }
.delay-200 { animation-delay: 0.1s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in, .spinner-sm { animation: none; }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 1200px) {
  .visitas-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .visitas-page {
    padding: 20px;
  }

  .page-header {
    padding: 18px;
  }

  .card-flat {
    padding: 20px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/Visitas/RegistrarVisita.vue